import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $rtkApi } from 'shared/api/rtkApi';
import { notify } from 'shared';
import { useStoreUiControls } from 'shared/lib';
import { CONTROL_IDS } from 'shared/consts';

type Entry = {
  route: 'CANONICAL' | 'LEGACY' | 'SELECT_CONNECTION' | 'UNAVAILABLE';
  setupUrl: string | null;
};
const api = $rtkApi.injectEndpoints({
  endpoints: (build) => ({
    openSetupEntry: build.mutation<
      Entry,
      { connectionId?: number; locale: string }
    >({
      query: (body) => ({ url: '/api/xray/setup', method: 'POST', body }),
    }),
  }),
});

export const SetupEntryGate: React.FC<{
  connectionId?: number;
  children: React.ReactNode;
}> = ({ connectionId, children }) => {
  const { t, i18n } = useTranslation();
  const [open, { reset }] = api.useOpenSetupEntryMutation();
  const { close } = useStoreUiControls(CONTROL_IDS.GUIDE);
  const [legacy, setLegacy] = useState(false);
  const started = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    open({
      connectionId,
      locale: i18n.language?.split('-')[0] === 'ru' ? 'ru' : 'en',
    })
      .unwrap()
      .then((entry) => {
        if (!mounted.current) return;
        if (entry.route === 'LEGACY') setLegacy(true);
        else if (entry.route === 'CANONICAL' && entry.setupUrl)
          window.location.assign(entry.setupUrl);
        else {
          notify.info(
            t(
              entry.route === 'SELECT_CONNECTION'
                ? 'connection.list'
                : 'shared.error.base'
            )
          );
          close();
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        notify.error(t('shared.error.base'));
        close();
      })
      .finally(reset);
  }, [connectionId, open, reset, close, i18n.language, t]);
  return legacy ? <>{children}</> : <p role="status">{t('setup.loading')}</p>;
};
